import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getSolutionTree, getCategoryAdvantage, normalizeSolutionTree, type SolutionCategory } from "@/api/solutionApi";
import { getProducts } from "@/api/productApi";
import { getCases } from "@/api/caseApi";
import { assetUrl } from "@/utils/assetUrl";
import { getAccentColor, CASE_SOLUTION_MAP } from "@/utils/solutionDisplay";
import { ACTIVE_CATEGORY_SET } from "@/utils/solutionCategory";
import { selectRelatedCases } from "@/utils/solutionList";
import { readPreload, buildPreloadKey } from "@/api/preload";
import { DynamicCasesRow, SubSceneGridRow, type SubScene } from "./SolutionsShared";
import AiBusinessPanel from "../ai-business/AiBusinessPanel";

interface Props {
  blockRefs: Record<string, React.RefObject<HTMLDivElement | null>>;
  activeTab: string;
  onTabChange: (id: string) => void;
}

/** 请求超时后视为失败（Requirements 7.6, 10.5, 11.8） */
const FRONT_SOLUTIONS_FETCH_TIMEOUT_MS = 10000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("timeout")), ms);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

// ─── Sub-scene definitions ────────────────────────────────────────────────────
// SubScene 类型与子方案卡片网格（SubSceneGridRow）见 ./SolutionsShared，
// 与 AI 业务专属面板（AiBusinessPanel）共用。

// ─── Solution Intro Section ───────────────────────────────────────────────────

interface IntroSectionProps {
  accentColor: string;
  title: string;
  desc: string;
  bgImage: string;
}

/**
 * 简化后的方案简介板块：仅依赖接口分类节点可提供的字段
 * （title/desc/image），不再展示 highlights/stats 这类无对应数据源的营销文案。
 */
function IntroSection({ accentColor, title, desc, bgImage }: IntroSectionProps) {
  return (
    <div className="relative rounded-3xl overflow-hidden mb-6">
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img src={assetUrl(bgImage)} alt="" className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${accentColor}90 0%, ${accentColor}60 40%, rgba(22,34,54,0.65) 100%)` }}></div>
      </div>
      <div className="relative z-10 p-10 lg:p-14">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 border border-white/20 bg-white/10">
            <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></div>
            <span className="text-white/80 text-xs font-medium tracking-wider">方案简介</span>
          </div>
          <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 leading-snug">{title}</h3>
          <p className="text-white/75 text-sm leading-relaxed">{desc}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Architecture Section ─────────────────────────────────────────────────────

interface ArchItem {
  icon: string;
  name: string;
  desc: string;
  /** 关联产品（从 advantageData.arch 解析得到，前台以灰字链接展示） */
  relatedProducts?: { id: number; name: string }[];
}

interface ArchTier {
  label: string;
  items: ArchItem[];
}

interface ArchSectionProps {
  accentColor: string;
  archTitle: string;
  archTiers: ArchTier[];
}

function ArchSection({ accentColor, archTitle, archTiers }: ArchSectionProps) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-8 lg:p-10 mb-6">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-1 h-6 rounded-full" style={{ backgroundColor: accentColor }}></div>
        <h4 className="text-lg font-bold text-[#0d1f3c]">{archTitle}</h4>
      </div>
      <div className="space-y-6">
        {archTiers.map((tier, ti) => (
          <div key={tier.label} className="relative">
            {/* Tier connector line */}
            {ti < archTiers.length - 1 && (
              <div className="absolute left-5 top-full w-px h-6 z-10" style={{ backgroundColor: `${accentColor}30` }}></div>
            )}
            <div
              className="flex items-center gap-3 px-5 py-3 rounded-xl mb-4 w-fit"
              style={{ backgroundColor: `${accentColor}10`, border: `1px solid ${accentColor}20` }}
            >
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: accentColor }}></div>
              <span className="text-sm font-bold" style={{ color: accentColor }}>{tier.label}</span>
            </div>
            <div
              className="grid gap-4"
              style={{ gridTemplateColumns: `repeat(${Math.min(tier.items.length, 3)}, minmax(0, 1fr))` }}
            >
              {tier.items.map((item) => (
                <div
                  key={item.name}
                  className="flex items-start gap-4 p-5 rounded-2xl border transition-all duration-200 hover:border-opacity-60"
                  style={{ borderColor: `${accentColor}18`, backgroundColor: `${accentColor}04` }}
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ backgroundColor: `${accentColor}12` }}
                  >
                    <i className={`${item.icon} text-lg`} style={{ color: accentColor }}></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h5 className="text-sm font-bold text-[#0d1f3c] mb-1">{item.name}</h5>
                    <p className="text-xs text-gray-400 leading-relaxed">{item.desc}</p>
                    {item.relatedProducts && item.relatedProducts.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                        {item.relatedProducts.map((p) => (
                          <Link
                            key={p.id}
                            to={`/products/${p.id}`}
                            className="text-xs text-gray-400 hover:text-[color:var(--acc)] transition-colors underline-offset-2 hover:underline"
                            style={{ ["--acc" as any]: accentColor }}
                            title={p.name}
                          >
                            {p.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Advantages Section (BIG) ─────────────────────────────────────────────────

interface AdvantageItem {
  icon: string;
  title: string;
  value: string;
  desc: string;
  detail: string;
  step: string;
}

interface AdvantagesSectionProps {
  accentColor: string;
  sectionTitle: string;
  sectionSubtitle: string;
  items: AdvantageItem[];
  valueSummary: { icon: string; title: string; desc: string }[];
}

function AdvantagesSection({ accentColor, sectionTitle, sectionSubtitle, items, valueSummary }: AdvantagesSectionProps) {
  return (
    <div className="mb-6">
      {/* Big advantages block */}
      <div
        className="rounded-3xl overflow-hidden"
        style={{ background: `linear-gradient(160deg, #2a3a52 0%, #1e2d45 60%, ${accentColor}20 100%)` }}
      >
        {/* Header */}
        <div className="px-10 lg:px-14 pt-12 pb-8 text-center border-b border-white/8">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 border"
            style={{ borderColor: `${accentColor}40`, backgroundColor: `${accentColor}15` }}
          >
            <i className="ri-star-line text-xs" style={{ color: accentColor }}></i>
            <span className="text-xs font-semibold tracking-wider" style={{ color: accentColor }}>核心优势</span>
          </div>
          <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3">{sectionTitle}</h3>
          <p className="text-white/50 text-sm max-w-lg mx-auto">{sectionSubtitle}</p>
        </div>

        {/* Advantage cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
          {items.map((item) => (
            <div
              key={item.title}
              className="group flex flex-col gap-4 px-8 py-10 transition-all duration-300 cursor-default"
              style={{ backgroundColor: "rgba(22,34,54,0.55)" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = `${accentColor}25`; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = "rgba(22,34,54,0.55)"; }}
            >
              <span className="text-xs font-bold tracking-widest" style={{ color: `${accentColor}60` }}>{item.step}</span>
              <div
                className="w-14 h-14 flex items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110"
                style={{ backgroundColor: `${accentColor}20`, border: `1px solid ${accentColor}35` }}
              >
                <i className={`${item.icon} text-2xl`} style={{ color: accentColor }}></i>
              </div>
              <div className="text-3xl font-extrabold text-white">{item.value}</div>
              <div>
                <h5 className="text-base font-bold text-white mb-2">{item.title}</h5>
                <p className="text-sm text-white/55 leading-relaxed">{item.desc}</p>
              </div>
              <div
                className="mt-auto pt-4 border-t text-xs leading-relaxed"
                style={{ borderColor: `${accentColor}35`, color: "rgba(255,255,255,0.65)" }}
              >
                {item.detail}
              </div>
            </div>
          ))}
        </div>

        {/* Value summary row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5">
          {valueSummary.map((v) => (
            <div
              key={v.title}
              className="flex items-start gap-5 px-10 py-8"
              style={{ backgroundColor: `${accentColor}18` }}
            >
              <div
                className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <i className={`${v.icon} text-xl`} style={{ color: accentColor }}></i>
              </div>
              <div>
                <h5 className="text-sm font-bold text-white mb-1.5">{v.title}</h5>
                <p className="text-xs text-white/55 leading-relaxed">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Cases row ────────────────────────────────────────────────────────────────
// 关联案例行（DynamicCasesRow）见 ./SolutionsShared，与 AI 业务专属面板共用。

// ─── Panel component ──────────────────────────────────────────────────────────

interface CategoryPanelProps {
  category: SolutionCategory;
  advantageData: any | null;
  apiCases: any[];
  apiProducts: { id: number; name: string }[];
  onNavigate: (id: string) => void;
}

// 把后端配置的 arch (JSON) 解析为 ArchSection 所需的 tiers + 解析 productIds
function parseArchFromAdvantage(
  advantageData: any | null,
  apiProducts: { id: number; name: string }[],
): { title: string; tiers: ArchTier[] } | null {
  if (!advantageData?.arch) return null;
  let raw = advantageData.arch;
  try {
    if (typeof raw === "string") raw = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "object") return null;

  const productMap = new Map(apiProducts.map((p) => [p.id, p.name]));
  const tiersRaw = Array.isArray(raw.tiers) ? raw.tiers : [];
  if (tiersRaw.length === 0) return null;

  const tiers: ArchTier[] = tiersRaw.map((t: any, ti: number) => ({
    label: t.label || `第 ${ti + 1} 层`,
    items: (Array.isArray(t.items) ? t.items : []).map((it: any) => ({
      icon: it.icon || "",
      name: it.name || "",
      desc: it.desc || "",
      relatedProducts: (Array.isArray(it.productIds) ? it.productIds : [])
        .map((pid: any) => Number(pid))
        .filter((pid: number) => !Number.isNaN(pid))
        .map((pid: number) => ({ id: pid, name: productMap.get(pid) || "" }))
        .filter((p: { id: number; name: string }) => !!p.name),
    })),
  }));

  return { title: raw.title || "", tiers };
}

/**
 * 合并后的通用分类面板：图标/标签/标题/副标题取自接口分类节点，主题色取 getAccentColor。
 * 方案卡片列表以 category.subSolutions 为唯一数据源；无已发布方案时展示空态但仍渲染
 * 分类标题与优势板块。优势板块数据为空时整块隐藏（不再有任何硬编码默认内容回退）。
 */
export function CategoryPanel({ category, advantageData, apiCases, apiProducts, onNavigate }: CategoryPanelProps) {
  const color = getAccentColor(category.id);
  const subs: SubScene[] = [...(category.subSolutions || [])]
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    .map((sub) => ({
      id: String(sub.id),
      icon: (sub as any).icon || "",
      name: sub.name || "",
      desc: sub.desc || "",
      tags: ((sub as any).tags && (sub as any).tags.length > 0)
        ? (sub as any).tags.filter(Boolean)
        : (sub.features || []).map((f: any) => f.name).filter(Boolean),
      productCategory: String(sub.id),
      image: sub.image || "",
    }));

  const advTitle = advantageData?.title || "";
  const advSubtitle = advantageData?.subtitle || "";
  const advMainCards = advantageData?.mainCards;
  const advBottomCards = advantageData?.bottomCards;
  const hasAdvantageContent = !!(
    advantageData && (advTitle || advSubtitle || advantageData.arch || (advMainCards && advMainCards.length) || (advBottomCards && advBottomCards.length))
  );

  const items = (advMainCards || []).map((c: any, i: number) => ({
    icon: c.icon || "ri-shield-check-line",
    label: c.direction || "",
    value: c.metric || "",
    title: c.direction || "",
    desc: c.description || "",
    detail: c.supplement || "",
    step: String(i + 1).padStart(2, "0"),
  }));

  const valueSummary = (advBottomCards || []).map((c: any) => ({
    icon: c.icon || "ri-shield-check-line",
    title: c.metric || "",
    desc: c.description || "",
  }));

  // 关联案例：按 CASE_SOLUTION_MAP 配置的案例分类集合 + selectRelatedCases 过滤
  // （it-infrastructure 取 infrastructure/smart-integration，cloud-xinda/ai-business 暂无配置）
  const relatedCases = selectRelatedCases(apiCases, CASE_SOLUTION_MAP[category.id] ?? []);

  return (
    <div>
      {subs.length > 0 ? (
        <SubSceneGridRow scenes={subs} accentColor={color} onNavigate={onNavigate} />
      ) : (
        <div className="flex flex-col items-center justify-center py-16 mb-8 rounded-2xl border border-dashed border-gray-200 bg-white/60">
          <div className="w-12 h-12 flex items-center justify-center rounded-full mb-3" style={{ backgroundColor: `${color}15` }}>
            <i className="ri-inbox-line text-xl" style={{ color }}></i>
          </div>
          <p className="text-sm text-gray-400">暂无可查看方案</p>
        </div>
      )}

      {/* Intro with background image */}
      <IntroSection
        accentColor={color}
        title={category.title || category.tag || ""}
        desc={category.subtitle || category.desc || ""}
        bgImage={category.image || ""}
      />

      {/* Architecture（来自后台配置） */}
      {(() => {
        const customArch = parseArchFromAdvantage(advantageData, apiProducts);
        if (!customArch) return null;
        return (
          <ArchSection
            accentColor={color}
            archTitle={customArch.title || `${category.title || category.tag}方案体系`}
            archTiers={customArch.tiers}
          />
        );
      })()}

      {/* Big Advantages（数据为空或未按时返回时整块隐藏） */}
      {hasAdvantageContent && (
        <AdvantagesSection
          accentColor={color}
          sectionTitle={advTitle}
          sectionSubtitle={advSubtitle}
          items={items}
          valueSummary={valueSummary}
        />
      )}

      <DynamicCasesRow cases={relatedCases} accentColor={color} categoryId={category.id} />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SolutionsBlocks({ blockRefs, activeTab, onTabChange }: Props) {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);
  const tabBarRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [tabBarHeight, setTabBarHeight] = useState(0);
  const [solutionTree, setSolutionTree] = useState<SolutionCategory[]>(
    () => normalizeSolutionTree(readPreload<SolutionCategory[]>(buildPreloadKey("GET", "/api/front/solutions"))),
  );
  const [apiCases, setApiCases] = useState<any[]>(() => {
    const rows = readPreload<any[]>(
      buildPreloadKey("GET", "/api/front/cases", { pageNum: 1, pageSize: 100 }),
    ) as any;
    const list = rows?.rows ?? rows?.data ?? (Array.isArray(rows) ? rows : []);
    return list.map((c: any) => ({ ...c, tags: c.tags ?? [], metrics: c.metrics ?? [], summary: c.summary ?? c.description ?? "" }));
  });
  const [apiProducts, setApiProducts] = useState<{ id: number; name: string }[]>(() => {
    const list = readPreload<any[]>(buildPreloadKey("GET", "/api/front/products", { pageNum: 1, pageSize: 9999 }));
    return Array.isArray(list) ? list.map((p: any) => ({ id: Number(p.id), name: p.name })) : [];
  });
  const [advantageMap, setAdvantageMap] = useState<Record<string, any>>({});

  // TABS 由 GET /api/front/solutions 返回分类动态生成（按 sort 升序）
  const TABS = [...solutionTree]
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    .map((cat) => ({
      id: cat.id,
      icon: cat.icon,
      label: cat.tag || cat.title,
      color: getAccentColor(cat.id),
    }));

  const activeTabConfig = TABS.find((t) => t.id === activeTab);
  const activeCategory = solutionTree.find((c) => c.id === activeTab);

  useEffect(() => {
    getSolutionTree().then((res: any) => setSolutionTree(Array.isArray(res) ? res : [])).catch(() => {});
    withTimeout(getCases({ pageNum: 1, pageSize: 100 }), FRONT_SOLUTIONS_FETCH_TIMEOUT_MS).then((res: any) => {
      const rows = res.rows ?? res.data ?? (Array.isArray(res) ? res : []);
      setApiCases(rows.map((c: any) => ({ ...c, tags: c.tags ?? [], metrics: c.metrics ?? [], summary: c.summary ?? c.description ?? "" })));
    }).catch(() => {});
    getProducts().then((list: any) => {
      const arr = Array.isArray(list) ? list : [];
      setApiProducts(arr.map((p: any) => ({ id: Number(p.id), name: p.name })));
    }).catch(() => {});
    // 加载 Active_Category_Set 全部分类的核心优势配置，每个请求 10 秒未返回视为失败
    ACTIVE_CATEGORY_SET.forEach((cat) => {
      withTimeout(getCategoryAdvantage(cat), FRONT_SOLUTIONS_FETCH_TIMEOUT_MS)
        .then((res: any) => {
          const data = res?.data ?? res;
          // 只要后端返回任一字段（title/subtitle/mainCards/bottomCards/arch），就把 data 存入 map
          if (data && (data.title || data.subtitle || data.arch || (data.mainCards && data.mainCards.length) || (data.bottomCards && data.bottomCards.length))) {
            setAdvantageMap((prev) => ({ ...prev, [cat]: data }));
          }
        })
        .catch(() => {});
    });
  }, []);

  useEffect(() => {
    const tabEl = tabBarRef.current;
    if (!tabEl) return;
    const NAVBAR_H = 72;
    const check = () => {
      const rect = tabEl.getBoundingClientRect();
      setIsSticky(rect.bottom <= NAVBAR_H);
      setTabBarHeight(tabEl.offsetHeight);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check, { passive: true });
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, []);

  const handleTabChange = (id: string) => {
    onTabChange(id);
    setTimeout(() => {
      if (sectionRef.current) {
        const NAVBAR_H = 72;
        const top = sectionRef.current.getBoundingClientRect().top + window.scrollY - NAVBAR_H;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }, 50);
  };

  const handleNavigateToProduct = (sceneId: string) => {
    navigate(`/solutions/detail/${sceneId}`);
  };

  void tabBarHeight;

  const TabBarContent = () => (
    <div className="flex flex-col sm:flex-row gap-2 p-1.5 bg-gray-50 rounded-2xl border border-gray-100">
      {TABS.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className="flex-1 flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={
              isActive
                ? { backgroundColor: tab.color, color: "#fff" }
                : { backgroundColor: "transparent", color: "#6b7280" }
            }
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className={`${tab.icon} text-base`}></i>
            </div>
            <span className="text-sm font-bold">{tab.label}</span>
            {isActive && (
              <div className="w-4 h-4 flex items-center justify-center ml-0.5">
                <i className="ri-check-line text-white text-xs"></i>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );

  return (
    <div
      className="bg-[#f9fafb]"
      ref={(el) => {
        (sectionRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        Object.values(blockRefs).forEach((ref) => {
          if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
        });
      }}
      id="solutions-blocks"
    >
      {/* Sticky tab bar */}
      {isSticky && (
        <div
          className="fixed top-[72px] left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100"
          style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-2.5">
            <TabBarContent />
          </div>
        </div>
      )}

      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">

          {/* Section header */}
          <div className="text-center mb-10">
            <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-3">核心解决方案</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#0d1f3c] mb-4">
              选择适合您的
              <span style={{ color: activeTabConfig?.color }}> 解决方案</span>
            </h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto leading-relaxed">
              点击下方标签切换方案，点击子场景卡片可直接跳转查看相关产品
            </p>
          </div>

          {/* Inline tab bar */}
          <div ref={tabBarRef} className="mb-10">
            <TabBarContent />
          </div>

          {/* Panel content：ai-business 为客户确认的专属栏目首页（设计稿落地），
              其余分类沿用通用 CategoryPanel */}
          <div ref={contentRef}>
            <div key={activeTab}>
              {activeCategory && (
                activeTab === "ai-business" ? (
                  <AiBusinessPanel
                    category={activeCategory}
                    apiCases={apiCases}
                    onNavigate={handleNavigateToProduct}
                  />
                ) : (
                  <CategoryPanel
                    category={activeCategory}
                    advantageData={advantageMap[activeTab] ?? null}
                    apiCases={apiCases}
                    apiProducts={apiProducts}
                    onNavigate={handleNavigateToProduct}
                  />
                )
              )}
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}
